import { observer } from 'mobx-react-lite';
import * as React from 'react';
import BareContentEditableDivEditor from './BareContentEditableDivEditor';
import ReactEditor, { ReactEditorProps } from './ReactEditor';
import PlainTextEditor, { PlainTextEditorProps, PlainTextEditorHandle } from './PlainTextEditor';
import setIsFocusWorkaroundDivShown from '../actions/setIsFocusWorkaroundDivShown';
import { ComposeEditorType } from 'owa-mail-preferred-editor';
import { getActiveEditorType } from 'owa-mail-preferred-editor/lib/getActiveEditorType';
import { CompositeEditorViewState } from '../store/schema/CompositeEditorViewState';
import { isBrowserEdge, isBrowserIE } from 'owa-user-agent/lib/userAgent';

export interface CompositeEditorProps extends ReactEditorProps, PlainTextEditorProps {
    viewState: CompositeEditorViewState;
    isEditorDisabled?: boolean;
}

export default observer(function CompositeEditor(props: CompositeEditorProps) {
    const onEditorFocus = () => {
        setIsFocusWorkaroundDivShown(props.viewState, false /*isShown*/);
        if (originalOnFocus.current && originalOnFocus.current !== onEditorFocus) {
            originalOnFocus.current();
        }
    };
    const onEditorBlur = () => {
        setIsFocusWorkaroundDivShown(props.viewState, true /*isShown*/);
        if (originalOnBlur.current && originalOnBlur.current !== onEditorBlur) {
            originalOnBlur.current();
        }
    };
    props = {
        onFocus: onEditorFocus,
        onBlur: onEditorBlur,
        ...props,
    };
    const roosterEditor = React.useRef<ReactEditor>();
    const bareCEDEditor = React.useRef<BareContentEditableDivEditor>();
    const plainTextEditor = React.useRef<PlainTextEditorHandle>();
    const originalOnFocus = React.useRef<() => void>(props.onFocus);
    const originalOnBlur = React.useRef<() => void>(props.onBlur);
    // Workaround a behavior in Edge/IE
    // Add an extra DIV before and after editor to accept the focus by press TAB/Shift+Tab then use code to trigger the real focus into editor
    // So that the view will not do auto scroll
    const renderFocusWorkaroundDiv = (): JSX.Element => {
        return (
            (isBrowserEdge() || isBrowserIE()) &&
            props.viewState.isFocusWorkaroundDivShown && (
                <div onFocus={onFocusToWorkaroundDiv} tabIndex={0} />
            )
        );
    };
    const onFocusToWorkaroundDiv = React.useCallback(() => {
        if (roosterEditor.current) {
            roosterEditor.current.focus();
        } else if (plainTextEditor.current) {
            plainTextEditor.current.focus();
        }
    }, [roosterEditor.current, plainTextEditor.current]);

    const { viewState, classNames } = props;
    let activeEditor = getActiveEditorType(viewState);

    if (props.isEditorDisabled) {
        if (viewState.bodyType === 'HTML') {
            activeEditor = ComposeEditorType.RawContentEditableDivHTML;
        } else if (viewState.bodyType === 'Text') {
            activeEditor = ComposeEditorType.Textarea;
        }
    }

    let editor: JSX.Element = null;
    switch (activeEditor) {
        case ComposeEditorType.RoosterHTML:
            editor = <ReactEditor {...props} ref={roosterEditor} />;
            break;
        case ComposeEditorType.RawContentEditableDivHTML:
            editor = (
                <BareContentEditableDivEditor
                    isEditorDisabled={props.isEditorDisabled}
                    className={classNames}
                    viewState={viewState}
                    ref={bareCEDEditor}
                />
            );
            break;
        case ComposeEditorType.Textarea:
            editor = (
                <PlainTextEditor
                    {...props}
                    ref={plainTextEditor}
                    isEditorDisabled={props.isEditorDisabled}
                />
            );
            break;
    }
    return (
        <>
            {renderFocusWorkaroundDiv()}
            {editor}
            {renderFocusWorkaroundDiv()}
        </>
    );
});
